/**
 * SetupActivateModal — the "Setup & Activate" workflow for a pending company.
 * A SystemAdmin completes the org info + banking, invites the org's user(s), and activates.
 */
import React, { useState, useRef, useEffect } from 'react';
import { X, Plus, Trash2, Loader2, ShieldCheck, AlertCircle } from 'lucide-react';
import { US_STATES, COUNTRIES } from '../../constants/locations';
import { getErrorMessage } from '../../services/apiService';
import { maskPhoneInput } from '../../utils/validation';
import {
  setupAndActivateCompany, InvoiceFrequency, BankAccountType,
  PendingCompanyResponse, SetupActivateUserInvite,
} from '../../services/clientsAdminService';

interface Props {
  company: PendingCompanyResponse;
  onClose: () => void;
  onActivated: (name: string) => void;
}

const labelCls = 'block text-xs font-semibold text-[#475569] mb-1';
const inputCls = 'w-full px-3 py-2 rounded-lg border border-black/[0.1] bg-white text-sm text-[#0f172a] focus:outline-none focus:border-[#c41e3a]/50';

const splitName = (full: string | null): { first: string; last: string } => {
  const parts = (full || '').trim().split(/\s+/);
  return { first: parts[0] || '', last: parts.slice(1).join(' ') || '' };
};

const SetupActivateModal: React.FC<Props> = ({ company, onClose, onActivated }) => {
  const contact = splitName(company.contactName);

  const [name, setName] = useState(company.companyName);
  const [description, setDescription] = useState(company.description || '');
  const [streetAddress, setStreetAddress] = useState(company.streetAddress || '');
  const [city, setCity] = useState(company.city || '');
  const [state, setState] = useState(company.state || '');
  const [zipCode, setZipCode] = useState(company.zipCode || '');
  const [country, setCountry] = useState(company.country || 'United States');
  const [phone, setPhone] = useState(company.phone || '');

  const [autoPay, setAutoPay] = useState(true);
  const [invoiceFrequency, setInvoiceFrequency] = useState<InvoiceFrequency>(InvoiceFrequency.Monthly);
  const [bankName, setBankName] = useState('');
  const [bankAccountHolderName, setBankAccountHolderName] = useState('');
  const [bankRoutingNumber, setBankRoutingNumber] = useState('');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [bankAccountType, setBankAccountType] = useState<BankAccountType>(BankAccountType.Checking);

  const [users, setUsers] = useState<SetupActivateUserInvite[]>([
    { email: company.contactEmail || '', firstName: contact.first, lastName: contact.last, role: 'ClientAdmin' },
  ]);

  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // The submit button sits at the bottom of a tall scrollable modal; if validation fails,
  // scroll the error (rendered at the top) into view so it doesn't look like nothing happened.
  const errorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [error]);

  const updateUser = (i: number, patch: Partial<SetupActivateUserInvite>) =>
    setUsers((u) => u.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  const addUser = () => setUsers((u) => [...u, { email: '', firstName: '', lastName: '', role: 'ClientUser' }]);
  const removeUser = (i: number) => setUsers((u) => u.filter((_, idx) => idx !== i));

  const submit = async () => {
    setError('');

    // Required before an org can be activated.
    if (!name.trim()) { setError('Company name is required.'); return; }
    if (!streetAddress.trim() || !city.trim() || !state.trim() || !zipCode.trim() || !country.trim()) {
      setError('A full address is required (street, city, state, ZIP, and country).');
      return;
    }
    const cleanUsers = users.filter((u) => u.email.trim());
    if (!cleanUsers.length) { setError('Invite at least one user.'); return; }
    if (cleanUsers.some((u) => !u.firstName.trim() || !u.lastName.trim())) { setError('Every invited user needs a first and last name.'); return; }
    if (cleanUsers.some((u) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(u.email.trim()))) { setError('Every invited user needs a valid email address.'); return; }

    setSaving(true);
    try {
      await setupAndActivateCompany(company.clientId, {
        name: name.trim(),
        description,
        streetAddress, city, state, zipCode, country, phone,
        autoPay,
        invoiceFrequency,
        bankName, bankAccountHolderName, bankRoutingNumber,
        bankAccountNumber: bankAccountNumber.trim() || undefined,
        bankAccountType,
        users: cleanUsers,
      });
      onActivated(name.trim());
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/40 backdrop-blur-sm overflow-y-auto py-8 px-4">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl my-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-black/[0.06] sticky top-0 bg-white rounded-t-2xl">
          <h2 className="text-lg font-bold text-[#0f172a] flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#c41e3a]" /> Onboard — {company.companyName}
          </h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-black/5 text-[#475569]"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-6 py-5 space-y-6">
          {error && (
            <div ref={errorRef} className="p-3 rounded-lg border bg-red-50 border-red-200 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Org info */}
          <section>
            <h3 className="text-sm font-bold text-[#0f172a] mb-3">Organization</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><label className={labelCls}>Company name</label><input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} /></div>
              <div className="col-span-2"><label className={labelCls}>Description</label><input className={inputCls} value={description} onChange={(e) => setDescription(e.target.value)} /></div>
              <div className="col-span-2"><label className={labelCls}>Street address</label><input className={inputCls} value={streetAddress} onChange={(e) => setStreetAddress(e.target.value)} /></div>
              <div><label className={labelCls}>City</label><input className={inputCls} value={city} onChange={(e) => setCity(e.target.value)} /></div>
              <div><label className={labelCls}>State</label>
                <select className={inputCls} value={state} onChange={(e) => setState(e.target.value)}>
                  <option value="">—</option>
                  {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
                </select>
              </div>
              <div><label className={labelCls}>ZIP</label><input className={inputCls} value={zipCode} onChange={(e) => setZipCode(e.target.value)} /></div>
              <div><label className={labelCls}>Country</label>
                <select className={inputCls} value={country} onChange={(e) => setCountry(e.target.value)}>
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="col-span-2"><label className={labelCls}>Phone</label><input className={inputCls} value={phone} placeholder="(555) 555-0100" onChange={(e) => setPhone(maskPhoneInput(e.target.value))} /></div>
            </div>
          </section>

          {/* Billing */}
          <section>
            <h3 className="text-sm font-bold text-[#0f172a] mb-3">Billing</h3>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 text-sm text-[#0f172a] col-span-1">
                <input type="checkbox" checked={autoPay} onChange={(e) => setAutoPay(e.target.checked)} className="w-4 h-4" /> AutoPay
              </label>
              <div><label className={labelCls}>Invoice frequency</label>
                <select className={inputCls} value={invoiceFrequency} onChange={(e) => setInvoiceFrequency(Number(e.target.value))}>
                  <option value={InvoiceFrequency.Monthly}>Monthly</option>
                  <option value={InvoiceFrequency.BiWeekly}>Bi-weekly</option>
                </select>
              </div>
              <div><label className={labelCls}>Bank name</label><input className={inputCls} value={bankName} onChange={(e) => setBankName(e.target.value)} /></div>
              <div><label className={labelCls}>Account holder</label><input className={inputCls} value={bankAccountHolderName} onChange={(e) => setBankAccountHolderName(e.target.value)} /></div>
              <div><label className={labelCls}>Routing number</label><input className={inputCls} value={bankRoutingNumber} onChange={(e) => setBankRoutingNumber(e.target.value)} /></div>
              <div><label className={labelCls}>Account number</label><input className={inputCls} value={bankAccountNumber} onChange={(e) => setBankAccountNumber(e.target.value)} /></div>
              <div><label className={labelCls}>Account type</label>
                <select className={inputCls} value={bankAccountType} onChange={(e) => setBankAccountType(Number(e.target.value))}>
                  <option value={BankAccountType.Checking}>Checking</option>
                  <option value={BankAccountType.Savings}>Savings</option>
                </select>
              </div>
            </div>
          </section>

          {/* Users to invite */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-[#0f172a]">Invite users</h3>
              <button onClick={addUser} className="text-xs text-[#c41e3a] font-medium inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add user</button>
            </div>
            <div className="space-y-2">
              {users.map((u, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <input className={inputCls + ' col-span-3'} placeholder="First" value={u.firstName} onChange={(e) => updateUser(i, { firstName: e.target.value })} />
                  <input className={inputCls + ' col-span-3'} placeholder="Last" value={u.lastName} onChange={(e) => updateUser(i, { lastName: e.target.value })} />
                  <input className={inputCls + ' col-span-3'} placeholder="Email" value={u.email} onChange={(e) => updateUser(i, { email: e.target.value })} />
                  <select className={inputCls + ' col-span-2'} value={u.role} onChange={(e) => updateUser(i, { role: e.target.value as 'ClientAdmin' | 'ClientUser' })}>
                    <option value="ClientAdmin">Admin</option>
                    <option value="ClientUser">User</option>
                  </select>
                  <button onClick={() => removeUser(i)} disabled={users.length === 1} className="col-span-1 text-[#94a3b8] hover:text-red-600 disabled:opacity-30 flex justify-center"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <p className="text-xs text-[#94a3b8] mt-2">Each invited user gets an email to set their password. At least one Admin is required.</p>
          </section>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-black/[0.06] sticky bottom-0 bg-white rounded-b-2xl">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 rounded-lg border border-black/[0.1] text-[#475569] text-sm font-medium hover:bg-black/[0.03]">Cancel</button>
          <button onClick={submit} disabled={saving} className="px-5 py-2 rounded-lg bg-[#c41e3a] hover:bg-[#a01830] text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Activating…</> : <><ShieldCheck className="w-4 h-4" /> Activate Account</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SetupActivateModal;
